import { fetchPrivateApi } from '@/lib/api/apiServiceBase.ts';
import { TableDefinitionDto } from '@/components/table/types/TableDefinitionDto.ts';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import { FilterCriteriaDto } from '@/components/table/types/FilterCriteriaDto.tsx';
import { PagedDto } from '@/components/table/types/PagedDto.ts';
import { MessageListDto } from '@/features/dashboard/messages/types/MessageListDto.ts';
import { MessageDetailDto } from '@/features/dashboard/messages/types/MessageDetailDto.ts';
import { CreateCustomMessageRequest } from '@/features/dashboard/messages/types/CreateCustomMessageRequest.ts';
import { buildPageRequestParams } from '@/components/table/types/TableUtil.ts';

type Scope = { tour: string } | { booking: string };

function scopePath(scope: Scope): string {
  if ('tour' in scope) return `tours/${scope.tour}`;
  return `bookings/${scope.booking}`;
}

const messagesApi = {
  getTableDefinition: async (): Promise<TableDefinitionDto> => {
    return await fetchPrivateApi<TableDefinitionDto>(
      'GET',
      `messages/table-definition`,
    );
  },
  getMessages: async (
    pageRequest: PageRequest,
    filterCriteria: FilterCriteriaDto,
  ): Promise<PagedDto<MessageListDto>> => {
    const params = buildPageRequestParams(pageRequest);
    return await fetchPrivateApi<PagedDto<MessageListDto>>(
      'POST',
      `messages/all?${params.toString()}`,
      filterCriteria,
    );
  },
  getMessage: async (id: string): Promise<MessageDetailDto> => {
    return await fetchPrivateApi<MessageDetailDto>('GET', `messages/${id}`);
  },
  createMessage: async (
    request: CreateCustomMessageRequest,
  ): Promise<MessageDetailDto> => {
    return await fetchPrivateApi<MessageDetailDto>('POST', `messages`, request);
  },
  cancelMessage: async (id: string): Promise<void> => {
    return await fetchPrivateApi<void>('POST', `messages/${id}/cancel`);
  },
  retryMessage: async (id: string): Promise<void> => {
    return await fetchPrivateApi<void>('POST', `messages/${id}/retry`);
  },
  deleteMessage: async (id: string): Promise<void> => {
    return await fetchPrivateApi<void>('DELETE', `messages/${id}`);
  },
  getScopedTableDefinition: async (
    scope: Scope,
  ): Promise<TableDefinitionDto> => {
    return await fetchPrivateApi<TableDefinitionDto>(
      'GET',
      `${scopePath(scope)}/messages/table-definition`,
    );
  },
  getScopedMessages: async (
    scope: Scope,
    pageRequest: PageRequest,
    filterCriteria: FilterCriteriaDto,
  ): Promise<PagedDto<MessageListDto>> => {
    const params = buildPageRequestParams(pageRequest);
    return await fetchPrivateApi<PagedDto<MessageListDto>>(
      'POST',
      `${scopePath(scope)}/messages/all?${params.toString()}`,
      filterCriteria,
    );
  },
};

export default messagesApi;
